import {DragEndEvent} from '@dnd-kit/core';
import {arrayMove, horizontalListSortingStrategy} from '@dnd-kit/sortable';
import {
  CustomRequestOption,
  HelperText,
  isFile,
  showNotification,
  Upload,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {Lightbox, LightboxImage, useLightbox} from 'platform/lightbox';

import {useState} from 'react';

import {isNil, isNotNil, prop, sortBy} from 'ramda';

import {useLazyGetFileByIdQuery, useUploadFileMutation} from '@dms/api/base';
import {CampaignPromoPhoto} from '@dms/api/sales';
import {
  buildSortableItemId,
  DnDContext,
  parseDndItemId,
  Sortable,
  SortableWrapper,
} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {MAX_PROMO_PHOTO_SIZE_IN_BYTES} from '../../../constants/MAX_PROMO_PHOTO_SIZE_IN_BYTES';
import {MAX_PROMO_PHOTO_SIZE_MB} from '../../../constants/MAX_PROMO_PHOTO_SIZE_MB';
import {notifyUnsupportedImageFormat} from '../../../utils/notifyUnsupportedImageFormat';
import {PROMO_PHOTO_TILE_SIZE} from '../constants/PROMO_PHOTO_TILE_SIZE';
import {PromoPhotoTile} from './PromoPhotoTile';

const CONTAINER_ID = 'campaign-promo-photos';
const ALLOWED_MIME_TYPES_TO_UPLOAD = ['image/jpeg', 'image/png', 'image/jpg'].join(', ');

const setPromoPhotoPositions = (photos: CampaignPromoPhoto[]): CampaignPromoPhoto[] =>
  photos.map((photo, index) => ({...photo, position: index + 1}));

interface PromoPhotosFieldProps extends RequiredTestIdProps {
  value: CampaignPromoPhoto[];
  onChange: (next: CampaignPromoPhoto[]) => void;
  errorMessage?: string | Nullish;
}

export function PromoPhotosField(props: PromoPhotosFieldProps) {
  const [uploadFile] = useUploadFileMutation();
  const [getFileById] = useLazyGetFileByIdQuery();

  const [lightboxControls, {onOpen}] = useLightbox(CONTAINER_ID);
  const [lightboxImages, setLightboxImages] = useState<LightboxImage[]>([]);

  const appendPhoto = (fileId: string) => {
    const currentPhotos = sortBy(prop('position'), props.value);

    props.onChange(
      setPromoPhotoPositions([...currentPhotos, {fileId, position: currentPhotos.length}])
    );
  };

  const handleCustomRequest = (option: CustomRequestOption) => {
    if (!isFile(option.file)) {
      return;
    }
    const file = option.file;

    if (file.size > MAX_PROMO_PHOTO_SIZE_IN_BYTES) {
      showNotification.error(
        i18n.t('entity.photo.labels.maxUploadFileSize', {max: MAX_PROMO_PHOTO_SIZE_MB})
      );
      option.onError?.(new Error('File exceeds the maximum size'));
      return;
    }

    uploadFile({file})
      .unwrap()
      .then((uploaded) => {
        appendPhoto(uploaded.fileId);
        option.onSuccess?.({});
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        option.onError?.(new Error('Upload failed'));
      });
  };

  const handlePreview = (index: number) => {
    Promise.all(
      props.value.map((photo) =>
        getFileById({fileId: photo.fileId}, true)
          .unwrap()
          .then((file) => ({id: photo.fileId, url: file.originalUri}))
      )
    )
      .then((images) => {
        setLightboxImages(images);
        onOpen(index);
      })
      .catch(handleApiError);
  };

  const handleDelete = (fileId: string) => {
    props.onChange(setPromoPhotoPositions(props.value.filter((photo) => photo.fileId !== fileId)));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (isNil(over) || active.id === over.id) {
      return;
    }

    const {position: fromIndex} = parseDndItemId(active.id);
    const {position: toIndex} = parseDndItemId(over.id);

    if (isNotNil(fromIndex) && isNotNil(toIndex)) {
      props.onChange(setPromoPhotoPositions(arrayMove(props.value, fromIndex, toIndex)));
    }
  };

  return (
    <VStack spacing={2} align="flex-start">
      <Text size="small" color="secondary">
        {i18n.t('entity.salesCampaign.labels.promoPhotos')}
      </Text>

      <DnDContext onDragEnd={handleDragEnd}>
        <SortableWrapper
          containerId={CONTAINER_ID}
          strategy={horizontalListSortingStrategy}
          items={props.value.map((photo) => ({id: photo.fileId, url: photo.fileId}))}
        >
          <HStack spacing={3} wrap align="flex-start">
            {props.value.map((photo, index) => (
              <Sortable
                key={photo.fileId}
                disableDropAnimation
                id={buildSortableItemId(photo.fileId, CONTAINER_ID, photo.fileId, index)}
              >
                <PromoPhotoTile
                  fileId={photo.fileId}
                  position={index}
                  onPreview={() => handlePreview(index)}
                  onDelete={() => handleDelete(photo.fileId)}
                  data-testid={suffixTestId(`photo-${index}`, props)}
                />
              </Sortable>
            ))}

            <Box width={PROMO_PHOTO_TILE_SIZE} height={PROMO_PHOTO_TILE_SIZE}>
              <Upload
                type="card"
                accept={ALLOWED_MIME_TYPES_TO_UPLOAD}
                shouldValidateAccept
                customRequest={handleCustomRequest}
                onError={notifyUnsupportedImageFormat}
                uploadText={i18n.t('entity.salesCampaign.actions.uploadPhoto')}
                uploadingText={i18n.t('general.labels.uploadingWithDots')}
                uploadIcon="content/add"
                data-testid={suffixTestId('upload', props)}
              />
            </Box>
          </HStack>
        </SortableWrapper>
      </DnDContext>

      <Show when={isNotNil(props.errorMessage)}>
        <HelperText errorMessage={props.errorMessage} data-testid={suffixTestId('error', props)} />
      </Show>

      <Lightbox
        controls={lightboxControls}
        images={lightboxImages}
        data-testid={suffixTestId('lightbox', props)}
      />
    </VStack>
  );
}
